import { useCallback, useState } from "react";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";
import { formatCryptocomSymbol, useCryptocomWsConnection } from "@/hooks/exchanges/direct-ws/cryptocom";
import type { CryptocomOrderBookMessage } from "@/hooks/exchanges/direct-ws/cryptocom/types";

const EXCHANGE_ID = "cryptocom";

/**
 * Hook for subscribing to Crypto.com orderbook updates via shared WebSocket.
 * Crypto.com sends full orderbook snapshots at regular intervals (~500ms).
 */
export const useCryptocomOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [orderBookError, setOrderBookError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      const symbol = formatCryptocomSymbol(pairSymbol);
      const channel = `book.${symbol}.50`;

      ws.send(JSON.stringify({ id: 1, method: "subscribe", params: { channels: [channel] } }));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: 2, method: "unsubscribe", params: { channels: [channel] } }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;
      const expectedSubscription = `book.${formatCryptocomSymbol(pairSymbol)}.50`;

      try {
        const typed = msg as CryptocomOrderBookMessage;

        if (typed.result?.subscription === expectedSubscription && typed.result?.data?.[0]) {
          const data = typed.result.data[0];
          const rawBids = data.bids;
          const rawAsks = data.asks;

          if (rawBids || rawAsks) {
            const bids: [number, number][] = (rawBids || [])
              .slice(0, 20)
              .map(([price, size]) => [parseFloat(price), parseFloat(size)]);

            const asks: [number, number][] = (rawAsks || [])
              .slice(0, 20)
              .map(([price, size]) => [parseFloat(price), parseFloat(size)]);

            setOrderBookData({ timestamp: Date.now(), exchangeId: EXCHANGE_ID, pairSymbol, bids, asks });
            setOrderBookError(null);
          }
        }
      } catch {
        setOrderBookError("Failed to parse orderbook update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useCryptocomWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error: orderBookError ?? connectionError,
  };
};
